import dayjs from 'dayjs';
import { useState } from 'react';

import { useBookings, type Booking, type BookingList } from '@/api/bookings';
import { Button, Card, EmptyState } from '@/components/PageHeading';
import { useDebounced } from '@/components/useDebounced';
import {
  boardLine,
  bookingLists,
  groupByDay,
  lateBanner,
  listOf,
  listRow,
  rowDetail,
  weekLabel,
  weekOf,
  weekStrip,
  type ListRow,
  type RowAction,
  type RowDetail,
} from '@core/bookings/bookingLists';
import type { BoardStart } from '@core/bookings/registerViews';
import { normalizeScan } from '@core/scan';

/** What a row's button asks the page to do. Call is a `tel:` link and never reaches the page. */
export type BoardIntent = Exclude<RowAction, 'call'>;

const actionLabel: Record<RowAction, string> = {
  call: 'Call',
  takeBack: 'Take back',
  handOver: 'Hand over',
  markReady: 'Mark ready',
};

const emptyText: Record<BookingList, string> = {
  Upcoming: 'Nothing is booked to go out.',
  WithCustomers: 'Nothing is out with a customer.',
  Done: 'No finished bookings yet.',
};

const pageSize = 25;

/**
 * The Bookings board on the website — the phone's Bookings tab ("B with C", 2026-10-15), brought to
 * the web with the rest of the booking journey (owner, 2026-10-22). It replaced the register's seven
 * saved-view tabs.
 *
 * <p>
 * Three lists by where the garments are — <strong>Coming · Out · Done</strong>, each the server's own
 * filter and order (`BookingList`). On Out, anything late is a <strong>red banner</strong> at the
 * top. Under the tabs a <strong>week strip</strong> counts what goes out (Coming) or comes back (Out)
 * each day, from the server's own list for that week; clicking a day shows only that day. Each
 * booking then says its money and offers its next step — Call, Take back, Hand over, Mark ready — so
 * most are dealt with from here; anything after this week is a quiet row under "Later". Every rule
 * is the core's `bookings/bookingLists.ts`, the phone's own.
 * </p>
 *
 * <p>
 * It reads its own lists, and hands every write back to the page as an intent, which is the split
 * `BookingsPage` keeps for every screen it shows.
 * </p>
 */
export function BookingBoard({
  branchId,
  today,
  currency,
  start,
  initialSearch = '',
  mayAct,
  busyId,
  onOpen,
  onIntent,
}: {
  branchId: number | undefined;
  today: string;
  currency: string;
  /** The list and day to open on — a Today card's view through `boardStart`. */
  start: BoardStart;
  /** A code the global find resolved, already in the search box. */
  initialSearch?: string;
  /** May write to bookings (`RentalOrder:Edit`) — a role without it sees Call only. */
  mayAct: boolean;
  /** The booking whose Mark ready is being sent. */
  busyId: number | null;
  onOpen: (booking: Booking) => void;
  onIntent: (intent: BoardIntent, booking: Booking) => void;
}) {
  const [list, setList] = useState<BookingList>(start.list);
  // A day of the strip, "late" for the banner's "Show the late", or the whole list.
  const [day, setDay] = useState<string | null>(start.day);
  const [inAlteration, setInAlteration] = useState(start.inAlteration);
  const [weekStart, setWeekStart] = useState(
    () => weekOf(start.day && start.day !== 'late' ? start.day : today)[0] ?? today,
  );
  const [typed, setTyped] = useState(initialSearch);
  const [pages, setPages] = useState(1);
  const search = normalizeScan(useDebounced(typed.trim()));
  const searching = search.length >= 2;
  const board = !searching && list !== 'Done';

  const filters = searching
    ? { search }
    : {
        list,
        ...(inAlteration ? { inAlteration: true } : {}),
        ...(day === 'late' ? { overdue: true } : day ? { dayFrom: day, dayTo: day } : {}),
      };
  const register = useBookings(branchId, { ...filters, pageSize: pageSize * pages });
  const rows = register.data?.items ?? [];

  const week = weekOf(weekStart);
  const thisWeekEnd = weekOf(today)[6] ?? today;
  const weekBookings = useBookings(
    branchId,
    { list, dayFrom: week[0], dayTo: week[6], pageSize: 100 },
    board,
  );
  const late = useBookings(
    branchId,
    { list: 'WithCustomers', overdue: true, pageSize: 50 },
    board && list === 'WithCustomers',
  );
  const banner = list === 'WithCustomers' ? lateBanner(late.data?.items ?? [], currency) : null;

  // The server's counts for the two lists that are worked, never a page's length.
  const comingCount = useBookings(branchId, { list: 'Upcoming', pageSize: 1 }).data?.total;
  const outCount = useBookings(branchId, { list: 'WithCustomers', pageSize: 1 }).data?.total;
  const counts: Record<BookingList, number | undefined> = {
    Upcoming: comingCount,
    WithCustomers: outCount,
    Done: undefined,
  };

  const rowOf = (booking: Booking): ListRow =>
    listRow(booking, searching ? listOf(booking.stage) : list, today);
  const groups = searching
    ? []
    : groupByDay(rows, rowOf, list, today, day ? {} : { laterAfter: thisWeekEnd }).filter(
        // The late ones are the banner's, unless the banner's own "Show the late" asked for them.
        (group) => !(group.late && banner && day !== 'late'),
      );

  const choose = (next: BookingList) => {
    setList(next);
    setDay(null);
    setInAlteration(false);
    setPages(1);
  };

  const pickDay = (next: string | null) => {
    setDay(next);
    setPages(1);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="border-border-color flex flex-wrap items-end gap-x-6 gap-y-2 border-b">
        {!searching && (
          <div role="tablist" className="flex gap-6">
            {bookingLists.map((option) => {
              const on = option.id === list;
              const count = counts[option.id];

              return (
                <button
                  key={option.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => choose(option.id)}
                  className={`-mb-px flex cursor-pointer items-center gap-2 border-b-2 px-0.5 py-2.5 text-[14px] font-semibold ${
                    on ? 'border-primary text-primary' : 'text-default border-transparent'
                  }`}
                >
                  {option.label}
                  {count !== undefined && count > 0 && (
                    <span
                      className={`rounded-full px-2 py-px text-[11px] ${
                        on ? 'bg-primary-transparent text-primary' : 'bg-light text-default'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="relative mb-2 ms-auto w-80 max-sm:w-full">
          <i className="icon icon-search text-default pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-[13px]" />
          <input
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
              setPages(1);
            }}
            placeholder="Name, mobile or booking number"
            aria-label="Find a booking"
            className="border-border-color h-9 w-full rounded border bg-white ps-8 pe-2.5 text-[13px]"
          />
        </div>
      </div>

      {inAlteration && !searching && (
        <p className="text-default flex items-center gap-2 text-[13px]">
          Showing only bookings with a garment at the tailor.
          <button
            type="button"
            onClick={() => setInAlteration(false)}
            className="text-primary cursor-pointer font-semibold"
          >
            Show everything
          </button>
        </p>
      )}

      {board && banner && (
        <div className="border-danger/40 bg-danger-transparent flex flex-wrap items-center gap-3 rounded-md border px-4 py-3">
          <i className="icon icon-clock text-danger" />
          <div className="min-w-0 flex-1">
            <p className="text-danger text-[13.5px] font-semibold">{banner.title}</p>
            <p className="text-danger text-[12.5px]">{banner.line}</p>
          </div>
          {banner.count === 1 ? (
            <>
              {banner.first.customerMobile && (
                <a
                  href={`tel:${banner.first.customerMobile}`}
                  className="btn-sm border-border-color inline-flex items-center gap-2 rounded border bg-white text-gray-900 hover:bg-light"
                >
                  <i className="icon icon-phone" />
                  Call
                </a>
              )}
              {mayAct && (
                <Button variant="primary" onClick={() => onIntent('takeBack', banner.first)}>
                  Take back
                </Button>
              )}
            </>
          ) : (
            <Button
              variant={day === 'late' ? 'secondary' : 'primary'}
              onClick={() => pickDay(day === 'late' ? null : 'late')}
            >
              {day === 'late' ? 'Show everything' : `Show the ${banner.count} late`}
            </Button>
          )}
        </div>
      )}

      {board && (
        <WeekStrip
          list={list}
          week={week}
          today={today}
          bookings={weekBookings.data?.items ?? []}
          chosen={day === 'late' ? null : day}
          onChoose={(date) => pickDay(day === date ? null : date)}
          onAll={() => pickDay(null)}
          onMove={(weeks) =>
            setWeekStart(
              dayjs(weekStart)
                .add(weeks * 7, 'day')
                .format('YYYY-MM-DD'),
            )
          }
        />
      )}

      {register.isLoading ? (
        <Card>
          <p className="text-default text-[13px]">Loading bookings…</p>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon="icon icon-calendar"
            title={
              searching
                ? `No booking matches “${search}”.`
                : day
                  ? 'Nothing on that day.'
                  : emptyText[list]
            }
          />
        </Card>
      ) : searching ? (
        <Group title={`${register.data?.total ?? rows.length} found`}>
          <Card flush>
            {rows.map((booking) => (
              <Row
                key={booking.id}
                booking={booking}
                row={rowOf(booking)}
                line={rowOf(booking).line}
                dated={false}
                detail={{ money: null, actions: [] }}
                mayAct={mayAct}
                busy={false}
                onOpen={onOpen}
                onIntent={onIntent}
              />
            ))}
          </Card>
        </Group>
      ) : (
        groups.map((group) => (
          <Group key={group.key} title={group.title} late={group.late}>
            <Card flush>
              {group.items.map((booking) => (
                <Row
                  key={booking.id}
                  booking={booking}
                  row={rowOf(booking)}
                  line={group.dated ? boardLine(booking, list) : rowOf(booking).line}
                  dated={group.dated}
                  detail={board ? rowDetail(booking, list, today) : { money: null, actions: [] }}
                  mayAct={mayAct}
                  busy={busyId === booking.id}
                  onOpen={onOpen}
                  onIntent={onIntent}
                />
              ))}
            </Card>
          </Group>
        ))
      )}

      {(register.data?.total ?? 0) > rows.length && (
        <div>
          <Button disabled={register.isFetching} onClick={() => setPages((was) => was + 1)}>
            Show more
          </Button>
        </div>
      )}
    </div>
  );
}

/** The week: how many go out or come back each day; a click shows only that day. */
function WeekStrip({
  list,
  week,
  today,
  bookings,
  chosen,
  onChoose,
  onAll,
  onMove,
}: {
  list: BookingList;
  week: string[];
  today: string;
  bookings: readonly Booking[];
  chosen: string | null;
  onChoose: (date: string) => void;
  onAll: () => void;
  onMove: (weeks: number) => void;
}) {
  const days = weekStrip(week, bookings, list, today);

  return (
    <Card>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="The week before"
          onClick={() => onMove(-1)}
          className="border-border-color flex size-8 shrink-0 cursor-pointer items-center justify-center rounded border hover:bg-light"
        >
          <i className="icon icon-chevron-left" />
        </button>

        <div className="grid min-w-0 flex-1 grid-cols-7 gap-1.5">
          {days.map((entry) => {
            const on = entry.date === chosen;

            return (
              <button
                key={entry.date}
                type="button"
                aria-pressed={on}
                aria-label={`${dayjs(entry.date).format('dddd D MMMM')}, ${entry.count}`}
                onClick={() => onChoose(entry.date)}
                className={`flex cursor-pointer flex-col items-center rounded-md py-1.5 ${
                  on
                    ? 'bg-primary text-white'
                    : entry.today
                      ? 'border-primary border-[1.5px]'
                      : 'hover:bg-light'
                }`}
              >
                <span className={`text-[11px] ${on ? 'text-white/85' : 'text-default'}`}>
                  {entry.weekday}
                </span>
                <span
                  className={`text-[15px] font-semibold ${
                    on
                      ? 'text-white'
                      : entry.late
                        ? 'text-danger'
                        : entry.past && list === 'Upcoming'
                          ? 'text-default'
                          : 'text-gray-900'
                  }`}
                >
                  {entry.day}
                </span>
                <span
                  className={`mt-0.5 min-w-6 rounded-full px-1.5 text-center text-[11px] font-semibold ${
                    entry.count === 0
                      ? 'text-transparent'
                      : on
                        ? 'text-primary bg-white'
                        : entry.late
                          ? 'bg-danger-transparent text-danger'
                          : 'bg-primary-transparent text-primary'
                  }`}
                >
                  {entry.count}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          aria-label="The week after"
          onClick={() => onMove(1)}
          className="border-border-color flex size-8 shrink-0 cursor-pointer items-center justify-center rounded border hover:bg-light"
        >
          <i className="icon icon-chevron-right" />
        </button>
      </div>

      <div className="text-default mt-2 flex items-center justify-between text-[12px]">
        <span>
          {chosen ? `${dayjs(chosen).format('ddd D MMM')} only` : weekLabel(week, list, today)}
        </span>
        {chosen && (
          <button
            type="button"
            onClick={onAll}
            className="text-primary cursor-pointer font-semibold"
          >
            All week
          </button>
        )}
      </div>
    </Card>
  );
}

function Group({
  title,
  late = false,
  children,
}: {
  title: string;
  late?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2
        className={`flex items-center gap-1.5 text-[11.5px] font-semibold tracking-wide uppercase ${
          late ? 'text-danger' : 'text-default'
        }`}
      >
        {late && <i className="icon icon-clock" />}
        {title}
      </h2>
      {children}
    </section>
  );
}

/** One booking: its date (or initials under a day heading), who, one line, money and its next step. */
function Row({
  booking,
  row,
  line,
  dated,
  detail,
  mayAct,
  busy,
  onOpen,
  onIntent,
}: {
  booking: Booking;
  row: ListRow;
  line: string;
  dated: boolean;
  detail: RowDetail;
  mayAct: boolean;
  busy: boolean;
  onOpen: (booking: Booking) => void;
  onIntent: (intent: BoardIntent, booking: Booking) => void;
}) {
  // Call needs no grant; the rest write, so a role that may not act sees only Call.
  const actions = detail.actions.filter((action) => action === 'call' || mayAct);
  const late = row.flag?.tone === 'danger';
  const last = actions.at(-1);

  return (
    <div className="border-border-color flex flex-wrap items-center gap-3 border-t px-4 py-3 first:border-t-0">
      <button
        type="button"
        onClick={() => onOpen(booking)}
        aria-label={`${booking.customerName}, ${line}${row.flag ? `, ${row.flag.text}` : ''}`}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-start"
      >
        {dated ? (
          <span className="bg-light flex size-10 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-gray-900">
            {initials(booking.customerName)}
          </span>
        ) : (
          <span
            className={`flex w-11 shrink-0 flex-col items-center rounded-md py-1 ${
              late ? 'bg-danger-transparent' : 'bg-light'
            }`}
          >
            <b className={`text-[16px] leading-tight ${late ? 'text-danger' : 'text-gray-900'}`}>
              {row.day}
            </b>
            <span className="text-default text-[10px]">{row.month}</span>
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-semibold text-gray-900 hover:underline">
            {booking.customerName}
            <span className="text-default font-mono-nums ms-2 text-[11.5px] font-normal">
              {booking.documentNumber || ''}
            </span>
          </span>
          <span className="text-default block truncate text-[12px]">{line}</span>
        </span>
      </button>

      {row.flag && (
        <span
          className={`rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold ${
            row.flag.tone === 'danger'
              ? 'bg-danger-transparent text-danger'
              : row.flag.tone === 'warning'
                ? 'bg-warning-transparent text-warning'
                : 'bg-light text-default'
          }`}
        >
          {row.flag.text}
        </span>
      )}

      {detail.money && (
        <span className="text-default bg-light rounded px-2.5 py-1 text-end text-[12px]">
          <span className="block text-gray-900">{detail.money.left}</span>
          {detail.money.right && <span className="block">{detail.money.right}</span>}
        </span>
      )}

      {actions.length > 0 && (
        <span className="flex gap-1.5">
          {actions.map((action) =>
            action === 'call' ? (
              booking.customerMobile ? (
                <a
                  key={action}
                  href={`tel:${booking.customerMobile}`}
                  className="btn-sm border-border-color inline-flex items-center gap-2 rounded border bg-white text-gray-900 hover:bg-light"
                >
                  <i className="icon icon-phone" />
                  {actionLabel[action]}
                </a>
              ) : null
            ) : (
              <Button
                key={action}
                variant={action === last ? 'primary' : 'secondary'}
                disabled={busy}
                onClick={() => onIntent(action, booking)}
              >
                {actionLabel[action]}
              </Button>
            ),
          )}
        </span>
      )}

      {actions.length === 0 && <i className="icon icon-chevron-right text-default" />}
    </div>
  );
}

function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}
